import React from 'react';
import type {StorefrontProduct} from '../types';
import {useCart} from '../context/CartContext';
import Button from '@/shared/components/Button';

function formatPrice(cents: number): {dollars: string; cents: string} {
    const total = cents / 100;
    const [d, c] = total.toFixed(2).split('.');
    return {dollars: d, cents: c};
}

interface ProductCardProps {
    product: StorefrontProduct;
}

const ProductCard: React.FC<ProductCardProps> = ({product}) => {
    const {addItem} = useCart();
    const price = formatPrice(product.price_cents);
    const memberPrice = product.member_price_cents
        ? (product.member_price_cents / 100).toFixed(2)
        : null;

    return (
        <div className="bg-mld-menu-background rounded-xl border border-mld-panel-border overflow-hidden flex flex-col hover:shadow-md transition-shadow h-[502px]">
            {/* Product image area — sits on page background so photos pop */}
            <div className="relative h-64 bg-mld-background flex items-center justify-center overflow-hidden">
                {product.image_url ? (
                    <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-contain p-4"
                    />
                ) : (
                    <div className="text-center px-4">
                        <div className="w-16 h-16 mx-auto mb-2 rounded-lg bg-mld-text-primary/10 flex items-center justify-center">
                            <svg className="w-8 h-8 text-mld-text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 3.104v5.714a2.25 2.25 0 0 1-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 0 1 4.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0 1 12 15a9.065 9.065 0 0 0-6.23.693L5 14.5m14.8.8 1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0 1 12 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
                            </svg>
                        </div>
                        <p className="text-sm font-semibold text-mld-text-primary">{product.name.split(' ').slice(0, 2).join(' ')}</p>
                    </div>
                )}
            </div>

            {/* Card body — elevated surface that auto-derives with theme */}
            <div className="p-5 flex flex-col flex-1">
                {/* Category badge — brand tertiary */}
                <span className="inline-block self-start px-4 py-1.5 text-xs font-medium text-white bg-mld-tertiary rounded-md mb-3">
                    {product.category}
                </span>

                <h3 className="text-base font-bold text-mld-text-primary mb-2 line-clamp-2">
                    {product.name}
                </h3>

                {/* Description with right-aligned "more info" on last line.
                    The gradient fades the trailing text into the card surface
                    so the link sits cleanly at the end of the truncated copy. */}
                <div className="text-sm text-mld-muted-text mb-4 flex-1 relative">
                    <p className="line-clamp-4">{product.description}</p>
                    {product.permalink && (
                        <span
                            className="absolute bottom-0 right-0 pl-6"
                            style={{background: 'linear-gradient(to right, transparent, rgb(var(--mld-menu-background)) 30%)'}}>
                            <a
                                href={product.permalink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-medium text-mld-primary hover:text-mld-text-primary transition-colors">
                                more info ›
                            </a>
                        </span>
                    )}
                </div>

                {/* Pricing + Add to cart */}
                <div className="flex items-end justify-between mt-auto">
                    <div>
                        <p className="text-xl font-bold text-mld-text-primary">
                            Starts at ${price.dollars}
                            <sup className="text-[10px] font-bold align-super">{price.cents}</sup>
                        </p>
                        {memberPrice && (
                            <p className="text-xs text-mld-text-primary mt-1">
                                ${memberPrice} with{' '}
                                <span className="font-medium text-mld-primary cursor-pointer">Membership</span>
                            </p>
                        )}
                    </div>
                    <Button
                        variant="primary"
                        size="md"
                        onClick={() => addItem(product)}
                        className="rounded-full whitespace-nowrap">
                        Add to cart
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ProductCard;
